import React from "react";
import { useMyEntitlements, usePublisherAndContacts } from "@seabrookstudios/pitch2table";
import { Path } from "../Path";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import { PublisherLogoSquare, PublisherLogoSquare2 } from "../publisher/PublisherLogo";
import { useAttachmentIdFromRoute, usePublisherIdFromRoute } from "../games/WithGameIdFromRoute";
import { formatFullDate, formatFollowUp, showAlarm, isStale } from "@seabrookstudios/pitch2table-core";
import { formatSubmissions } from "./model";
import { PublisherStatus } from "../games/PublisherStatus";
import { FollowUpDuePill } from "../games/FollowUpDuePill";
import { getSeverity } from "../games/publisher-interest/follow-up-severity";
import Error from "@mui/icons-material/Error";
import Warning from "@mui/icons-material/Warning";
import Tooltip from "@mui/material/Tooltip";
import WorkspacePremium from "@mui/icons-material/WorkspacePremium";
import {
  PublisherFitChip,
  ResearchPublisherFitTierChip,
} from "../games/publisher-interest/ResearchPublisherFitTierChip";

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").ContactBookPublisher} props.item
 */
export const MyPublisherListEntry = ({ item }) => {
  const publisherId = usePublisherIdFromRoute();
  const navigate = useNavigate();

  const selected = publisherId === item.id;

  const onClickHandler = () => {
    navigate(Path.publisher(item.id).link());
  };

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <PublisherLogoSquare2 publisher={item} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={item.name} secondary={formatSubmissions(item.games)} />
    </ListItemButton>
  );
};

const GenericNonMemberText =
  "Publisher has warnings. Upgrade to premium to see warnings at a glance, or navigate to the publisher page for more details.";

/**
 * @param {object} props
 * @param {import('@seabrookstudios/pitch2table-core').PublisherForApp} props.publisher
 * @param {React.ReactNode} [props.children] when supplied, renders children when there is no warning
 */
export const PublisherBannerIcon = ({ publisher, children = null }) => {
  const { data: entitlements } = useMyEntitlements();
  const isMember = entitlements?.isMember;
  const ErrorIcon = isMember ? Error : WorkspacePremium;
  const WarningIcon = isMember ? Warning : WorkspacePremium;

  if (publisher.defunct === true) {
    return (
      <Tooltip title={isMember ? "Publisher appears to be defunct." : GenericNonMemberText}>
        <ErrorIcon />
      </Tooltip>
    );
  }
  if (publisher.acceptingSubmissions === false) {
    return (
      <Tooltip title={isMember ? "Publisher is not accepting submissions." : GenericNonMemberText}>
        <WarningIcon />
      </Tooltip>
    );
  }
  if (isStale(publisher)) {
    return (
      <Tooltip
        title={
          isMember
            ? `Publisher has not published a game since ${publisher.yearLastGameReleased}.`
            : GenericNonMemberText
        }
      >
        <WarningIcon />
      </Tooltip>
    );
  }

  return children;
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherWithContactBookPublisher} props.item
 */
export const MergedPublisherListEntry = ({ item }) => {
  const [publisher, contactBookPublisher] = item;
  const publisherId = usePublisherIdFromRoute();
  const navigate = useNavigate();

  const selected = publisherId === publisher.id;

  const onClickHandler = () => {
    navigate(Path.publisher(publisher.id).link());
  };

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <PublisherLogoSquare2 publisher={publisher} width={48} height={48} />
      </ListItemAvatar>
      {contactBookPublisher ? (
        <ListItemText primary={publisher.name} secondary={formatSubmissions(contactBookPublisher.games)} />
      ) : (
        <ListItemText primary={publisher.name} secondary="" />
      )}
      <ListItemIcon>
        <PublisherBannerIcon publisher={publisher} />
      </ListItemIcon>
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").NonGameContract} props.item
 */
export const MyPublisherDocumentListEntry = ({ item }) => {
  const publisherId = usePublisherIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();
  const navigate = useNavigate();

  const selected = attachmentId === item.id;

  const onClickHandler = () => {
    navigate(Path.publisher(publisherId).document(item.id));
  };

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemText primary={item.name || "No title"} secondary={`Signed ${formatFullDate(item.signedDate)}`} />
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.item
 */
export const PublisherListEntry2 = ({ item }) => {
  const params = useParams();
  const navigate = useNavigate();

  const selected = params.publisherId === item.publisherId;

  const onClickHandler = () => {
    navigate(Path.game(item.gameId).publisher(item.publisherId));
  };

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <PublisherLogoSquare publisher={item} />
      </ListItemAvatar>
      <ListItemText primary={item.publisherName} secondary={formatFollowUp(item)} />
      {showAlarm(item.nextAction) && <FollowUpDuePill severity={getSeverity(item.nextAction)} />}
      <Box p={0.5} />
      <ListItemIcon>
        <PublisherStatus status={item.status} />
      </ListItemIcon>
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.item
 */
export const PublisherListEntryWithFit = ({ item }) => {
  const navigate = useNavigate();

  const onClickHandler = () => {
    navigate(Path.game(item.gameId).publisher(item.publisherId));
  };

  return (
    <ListItemButton alignItems="flex-start" onClick={onClickHandler}>
      <ListItemAvatar>
        <PublisherLogoSquare publisher={item} />
      </ListItemAvatar>
      <ListItemText primary={item.publisherName} secondary={formatFollowUp(item)} />
      {showAlarm(item.nextAction) && <FollowUpDuePill severity={getSeverity(item.nextAction)} />}
      <Box p={0.5} />
      {item.status !== "Declined" && (
        <ListItemIcon>
          <PublisherFitChip publisherId={item.publisherId} gameId={item.gameId} variant="outlined" />
        </ListItemIcon>
      )}
      <Box p={0.5} />
      <ListItemIcon>
        <PublisherStatus status={item.status} />
      </ListItemIcon>
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").DocumentListEntry} props.item
 */
export const PublisherContractListEntry = ({ item }) => {
  const attachmentId = useAttachmentIdFromRoute();
  const { data: publisherAndContacts = [] } = usePublisherAndContacts(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId} */ (item.refId)
  );
  const navigate = useNavigate();

  const selected = attachmentId === item.id;

  const onClickHandler = () => {
    navigate(Path.document(item.id));
  };

  const [publisher] = publisherAndContacts;

  if (!publisher) {
    return (
      <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
        <ListItemAvatar>
          <Skeleton width={48} height={48} />
        </ListItemAvatar>
        <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
      </ListItemButton>
    );
  }

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <PublisherLogoSquare2 publisher={publisher} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText
        primary={`${publisher.name} - ${item.name}`}
        secondary={`Signed: ${formatFullDate(item.signedDate)}`}
      />
    </ListItemButton>
  );
};
